<script setup lang="ts">
import { toast } from 'vue-sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LoaderCircle, Send } from 'lucide-vue-next';
import type { PortalActionBundle, PortalActionBundleCandidate } from '~/types/actionBundles';

const props = defineProps<{
    tenantId: string;
    clientId: string;
    clientName: string;
    candidates: PortalActionBundleCandidate[];
    loading?: boolean;
    dialogTitle?: string;
    dialogDescription?: string;
    emptyTitle?: string;
    emptyDescription?: string;
    submitLabel?: string;
    savingLabel?: string;
    cancelLabel?: string;
}>();
const emit = defineEmits<{ created: [bundle: PortalActionBundle, candidateKeys: string[]] }>();
const open = defineModel<boolean>('open', { default: false });

const title = ref('');
const message = ref('');
const selectedKeys = ref<string[]>([]);
const saving = ref(false);
const groups = computed(() => {
    const labels: Record<PortalActionBundleCandidate['type'], string> = {
        contract: 'Contracts', invoice: 'Invoices', form: 'Forms', approval: 'Approvals', task: 'Tasks', quote: 'Quotes', proposal: 'Proposals',
    };
    return Object.entries(labels).map(([type, label]) => ({
        type: type as PortalActionBundleCandidate['type'],
        label,
        items: props.candidates.filter(item => item.type === type),
    })).filter(group => group.items.length > 0);
});
const selectedItems = computed(() => props.candidates.filter(item => selectedKeys.value.includes(item.key)));
const canSubmit = computed(() => !saving.value && !props.loading && title.value.trim().length > 0 && selectedItems.value.length >= 2 && selectedItems.value.length <= 20);

watch(open, (isOpen) => {
    if (!isOpen) return;
    title.value = '';
    message.value = '';
    selectedKeys.value = [];
});

function toggleCandidate(key: string, checked: unknown) {
    if (checked === true) {
        if (selectedKeys.value.length >= 20) return;
        selectedKeys.value = [...selectedKeys.value, key];
    } else {
        selectedKeys.value = selectedKeys.value.filter(existing => existing !== key);
    }
}

async function createBundle() {
    if (!canSubmit.value) return;
    const { $auth } = useNuxtApp();
    if (!$auth.currentUser) {
        toast.error('Sign in again before sending this bundle.');
        return;
    }
    saving.value = true;
    try {
        const response = await $fetch<{ bundle: PortalActionBundle; notificationSent: boolean }>(`/api/clients/${encodeURIComponent(props.clientId)}/action-bundles`, {
            method: 'POST',
            query: { tenantId: props.tenantId },
            headers: { authorization: `Bearer ${await $auth.currentUser.getIdToken()}` },
            body: {
                title: title.value.trim(),
                message: message.value.trim(),
                items: selectedItems.value.map(item => ({
                    type: item.type,
                    resourceId: item.resourceId,
                    ...(item.sourceType ? { sourceType: item.sourceType } : {}),
                    ...(item.projectId ? { projectId: item.projectId } : {}),
                })),
            },
        });
        emit('created', response.bundle, selectedItems.value.map(item => item.key));
        open.value = false;
        toast.success(response.notificationSent ? 'Action bundle sent to the client portal.' : 'Action bundle created. Portal notification will retry if no recipient was available.');
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Could not send this action bundle.');
    } finally {
        saving.value = false;
    }
}
</script>

<template>
    <Dialog v-model:open="open">
        <DialogContent class="flex max-h-[85dvh] flex-col overflow-hidden sm:max-w-2xl">
            <DialogHeader>
                <DialogTitle>{{ dialogTitle || 'Send an action bundle' }}</DialogTitle>
                <slot name="description" :client-name="clientName">
                    <DialogDescription>{{ dialogDescription || `Group ready portal actions for ${clientName}. The bundle sends one consolidated notification, and each item keeps its own progress.` }}</DialogDescription>
                </slot>
            </DialogHeader>

            <div class="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain pr-1">
                <div class="space-y-2">
                    <Label for="action-bundle-title">Bundle name</Label>
                    <Input id="action-bundle-title" v-model="title" maxlength="120" placeholder="Website kickoff" />
                </div>
                <div class="space-y-2">
                    <Label for="action-bundle-message">Message for the client <span class="font-normal text-muted-foreground">(optional)</span></Label>
                    <Textarea id="action-bundle-message" v-model="message" maxlength="500" rows="2" placeholder="Your next steps are ready in one place." />
                </div>

                <div class="space-y-4">
                    <div class="flex items-center justify-between gap-3">
                        <Label>Actions to include</Label>
                        <Badge variant="outline">{{ selectedItems.length }} selected</Badge>
                    </div>
                    <div v-if="loading" class="py-6 text-sm text-muted-foreground">Loading available actions…</div>
                    <div v-else-if="groups.length" class="divide-y divide-border/60">
                        <section v-for="group in groups" :key="group.type" class="px-3">
                            <h3 class="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{{ group.label }}</h3>
                            <label v-for="item in group.items" :key="item.key" class="flex cursor-pointer items-start gap-3 border-t border-border/40 py-3 first:border-t-0">
                                <Checkbox :checked="selectedKeys.includes(item.key)" class="mt-0.5" @update:checked="toggleCandidate(item.key, $event)" />
                                <span class="min-w-0 flex-1">
                                    <span class="block text-sm font-medium text-foreground">{{ item.title }}</span>
                                    <span class="mt-0.5 block text-xs text-muted-foreground">{{ item.meta || item.description || group.label }}</span>
                                </span>
                            </label>
                        </section>
                    </div>
                    <slot v-else name="empty" :title="emptyTitle || 'No actions are ready yet'" :description="emptyDescription || 'Prepare actions for this client, then return here to send them together.'">
                        <p class="py-5 text-sm text-muted-foreground"><span class="block font-medium text-foreground">{{ emptyTitle || 'No actions are ready yet' }}</span>{{ emptyDescription || 'Prepare actions for this client, then return here to send them together.' }}</p>
                    </slot>
                    <p class="text-xs text-muted-foreground">Choose 2–20 items. New forms are added without separate emails. Actions already shared may have sent an earlier notification; this bundle adds one consolidated alert.</p>
                </div>
            </div>

            <DialogFooter class="shrink-0 border-t border-border/60 bg-background pt-4">
                <Button type="button" variant="outline" :disabled="saving" @click="open = false">{{ cancelLabel || 'Cancel' }}</Button>
                <Button type="button" :disabled="!canSubmit" @click="createBundle">
                    <LoaderCircle v-if="saving" class="mr-2 size-4 animate-spin" />
                    <Send v-else class="mr-2 size-4" />
                    {{ saving ? (savingLabel || 'Sending…') : (submitLabel || 'Send bundle') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
